/**
 * Google Analytics utility functions
 * Provides type-safe wrappers for gtag events
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Check if Google Analytics is loaded
 */
export const isGALoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isGALoaded()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  window.gtag('config', 'G-N8BMMNYB0C', {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    category?: string;
    label?: string;
    value?: number;
    [key: string]: any;
  }
): void => {
  if (!isGALoaded()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  window.gtag('event', eventName, eventParams);
};

/**
 * Track user authentication events
 */
export const trackAuthEvent = (action: 'signup' | 'login' | 'logout'): void => {
  trackEvent(action, {
    category: 'Authentication',
    label: action,
  });
};

/**
 * Track ticker-related events
 */
export const trackTickerEvent = (
  action: 'add' | 'remove' | 'view' | 'search',
  ticker?: string
): void => {
  trackEvent('ticker_action', {
    category: 'Ticker',
    action,
    label: ticker || 'unknown',
    ticker: ticker,
  });
};

/**
 * Track list-related events
 */
export const trackListEvent = (
  action: 'create' | 'switch' | 'delete' | 'rename',
  listName?: string
): void => {
  trackEvent('list_action', {
    category: 'List',
    action,
    label: listName || 'unknown',
    list_name: listName,
  });
};

/**
 * Track navigation events (switching between views)
 */
export const trackNavigationEvent = (
  view: 'daily' | 'technicals' | 'fundamentals' | 'analysts'
): void => {
  trackEvent('navigation', {
    category: 'Navigation',
    action: 'view_switch',
    label: view,
    view_type: view,
  });
};

/**
 * Track ticker detail view events
 */
export const trackTickerDetailView = (
  ticker: string,
  viewType: 'daily' | 'technicals' | 'fundamentals' | 'analysts'
): void => {
  trackEvent('ticker_detail_view', {
    category: 'Ticker Detail',
    action: 'view',
    label: `${ticker}_${viewType}`,
    ticker,
    view_type: viewType,
  });
};

/**
 * Track upgrade dialog events
 */
export const trackUpgradeDialog = (
  action: 'open' | 'close' | 'click_signup',
  dialogType?: 'signup' | 'ticker' | 'list'
): void => {
  trackEvent('upgrade_dialog', {
    category: 'Upgrade',
    action,
    label: dialogType || 'unknown',
    dialog_type: dialogType,
  });
};

/**
 * Track feedback submission
 */
export const trackFeedback = (action: 'submit' | 'cancel'): void => {
  trackEvent('feedback', {
    category: 'Feedback',
    action,
  });
};

/**
 * Track account management events
 */
export const trackAccountEvent = (
  action: 'view' | 'update' | 'delete_account'
): void => {
  trackEvent('account_action', {
    category: 'Account',
    action,
  });
};

/**
 * Track share list events
 */
export const trackShareList = (action: 'share' | 'copy_link'): void => {
  trackEvent('share_list', {
    category: 'Sharing',
    action,
  });
};

